import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import TENANT_DEFAULTS from '@sophocles/features/tenant/defaults';

import { buildBrandTokens } from './cssTokens';
import { getBuildTimeTenant } from './tenants';

const TENANT_STYLE_ID = 'tenant-brand-tokens';

const TenantThemeContext = createContext(null);

// Merge precedence (highest wins):
//   1. Backend tenant info (the existing `loadTenant` thunk)
//   2. Build-time tenant override (REACT_APP_TENANT)
//   3. The bundled `default` tenant
//
// Backend wins because it's the only source of truth for true
// multi-tenant deployments where one build serves many customers.
// Build-time lets a dev/staging instance brand itself before the
// backend is even configured.

const mergeTenant = (buildTime, backend) => {
  const merged = {
    id: backend?.id || buildTime?.id || 'default',
    brand: {
      name: backend?.organization_name || buildTime?.brand?.name || TENANT_DEFAULTS.organization_name,
      nameLong: backend?.organization_name_long || buildTime?.brand?.nameLong || TENANT_DEFAULTS.organization_name_long,
      primary: backend?.primary_color || buildTime?.brand?.primary || null,
      secondary: backend?.secondary_color || buildTime?.brand?.secondary || null,
      accent: buildTime?.brand?.accent || null,
    },
    // Five model-driven image slots from /api/tenant/.  Each is a real
    // uploaded URL (S3), so no sentinel-filtering needed — assign directly.
    // Build-time tenant JSON falls back to null for all slots (default.json),
    // which is correct: BrandLogo's fallback chain handles the bundled assets.
    logo: {
      large: backend?.logo_large || buildTime?.logo?.large || null,
      largeInverse: backend?.logo_large_inverse || buildTime?.logo?.largeInverse || null,
      small: backend?.logo_small || buildTime?.logo?.small || null,
      smallInverse: backend?.logo_small_inverse || buildTime?.logo?.smallInverse || null,
      favicon: backend?.favicon || buildTime?.logo?.favicon || null,
    },
    // DEPRECATED(customer-type-terms): tenant-layer-only, singular-only, and
    // currently unconsumed outside this module — hooks/relayLoadTerm.js
    // (resolveSiteTerm/resolveSiteGroupTerm via useSiteTerm/useSiteGroupTerm)
    // is now the authoritative site/site-group term source: it adds the
    // CustomerType-override layer above the tenant term and resolves plurals.
    // `customer` has no CustomerType-level equivalent, so it stays here.
    terms: {
      site: backend?.site_term || buildTime?.terms?.site || TENANT_DEFAULTS.site_term,
      siteGroup: backend?.sitegroup_term || buildTime?.terms?.siteGroup || TENANT_DEFAULTS.sitegroup_term,
      customer: backend?.customer_term || buildTime?.terms?.customer || TENANT_DEFAULTS.customer_term,
    },
    navbarColor: backend?.navbar_color || null,
  };
  return merged;
};

// Existing backend-driven tenant info selector.  Read defensively —
// the slice may not yet be hydrated on first render, in which case
// only build-time defaults apply.
const selectBackendTenant = state => state?.tenant?.info || null;

export const TenantThemeProvider = ({ children }) => {
  const buildTime = useMemo(() => getBuildTimeTenant(), []);
  const backend = useSelector(selectBackendTenant);

  const tenant = useMemo(() => mergeTenant(buildTime, backend), [buildTime, backend]);

  // Write brand CSS vars to a scoped <style> tag.  This wins over the
  // `:root { --brand-* }` defaults in cssTokens.js without rebuilding
  // the MUI theme, which keeps brand swaps cheap (no rerender storm).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const css = buildBrandTokens(tenant.brand);
    if (!css) return;
    let tag = document.getElementById(TENANT_STYLE_ID);
    if (!tag) {
      tag = document.createElement('style');
      tag.id = TENANT_STYLE_ID;
      document.head.appendChild(tag);
    }
    tag.textContent = css;
  }, [tenant.brand]);

  // Favicon swap — prefer the dedicated favicon slot, then the small
  // mark, then the full logo, so the browser tab carries brand.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const favicon = tenant.logo?.favicon || tenant.logo?.small || tenant.logo?.large;
    if (!favicon) return;
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/png';
    link.rel = 'shortcut icon';
    link.href = favicon;
    document.head.appendChild(link);
  }, [tenant.logo?.favicon, tenant.logo?.small, tenant.logo?.large]);

  // Document title — defer to brand name long so the OS task switcher
  // and browser tab read the tenant's name.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!tenant.brand?.nameLong) return;
    // Only set the prefix; pages can override the full title.
    document.documentElement.dataset.brandName = tenant.brand.nameLong;
  }, [tenant.brand?.nameLong]);

  return <TenantThemeContext.Provider value={tenant}>{children}</TenantThemeContext.Provider>;
};

TenantThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Public hook — consume tenant config from any component.
//
//   const { brand, logo, terms } = useTenantTheme();
//
// Components should reach for CSS vars (`var(--brand-primary)`,
// `var(--brand-name)`) when possible; this hook is for JSX that
// needs the raw values (e.g. an <img src={logo.large} />).
export const useTenantTheme = () => {
  const ctx = useContext(TenantThemeContext);
  if (!ctx) {
    // Provider not mounted — return the build-time defaults so
    // components don't crash if they render outside the tree (e.g.
    // tests, error boundaries).
    const buildTime = getBuildTimeTenant();
    return mergeTenant(buildTime, null);
  }
  return ctx;
};
